import { useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { ChevronDown, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NavLink } from './NavLink';
import { GlobalSearchBar } from './GlobalSearchBar';
import { UserMenu } from './UserMenu';
import { NotificationDropdown } from './NotificationDropdown';
import { HelpDropdown } from './HelpDropdown';
import { GetStartedPill } from './GetStartedPill';
import { GetStartedDropdown } from './GetStartedDropdown';
import { useGetStartedWidgetStore } from '@/features/get-started/stores/get-started-widget-store';
import { useOnboardingVariant } from '@/features/experiments';

import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavBreakpoints } from '@/hooks/useNavBreakpoints';
import { useMdmKillSwitch } from '@/hooks/useMdmKillSwitch';
import { isFreeEmail } from '@/features/survey/utils/free-email-domains';
import { useAppVariant } from '@/lib/app-variant';
import { ROLE_TYPES } from '@scio3/shared-types';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';

export function TopNavigation() {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const collapseLevel = useNavBreakpoints(navRef);
  const company = useAuthStore((s) => s.company);
  const userEmail = useAuthStore((s) => s.me?.email);
  const permissionsLoaded = useAuthStore((s) => s.me != null);
  const { isRootAdmin, isRootSuperAdmin, teamRole } = usePermissions();
  const variant = useAppVariant();
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;
  // feature.mdm is a new OptiEdge-Phase-1 flag not yet in the CompanyFeatures
  // TypeScript type (packages/shared-types finalized in Phase 3). Narrow
  // structural read so we stay type-safe without editing the shared type.
  const hasMdmFeature = (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
  // Global MDM kill-switch (appCfgs.mdmEnabled, appType: 'scio'). When true, Devices
  // tab is visible to everyone on the optisigns variant. When false/absent, only
  // accounts with company.feature.mdm=true see it (us + early adopters).
  const mdmKillSwitchOn = useMdmKillSwitch();
  // Drives the animated pill slot below — when the user minimizes the
  // Get Started widget, the slot grows from 0 to its natural width, smoothly
  // shifting the search bar leftward instead of snapping it.
  const getStartedWidgetState = useGetStartedWidgetStore((s) => s.widgetState);
  const isPillVisible = getStartedWidgetState === 'minimized';

  // scio3b-split: the top-nav get-started surface differs by variant.
  //   scio3b → new Help & resources dropdown (companion to the Get Started
  //            checklist).
  //   scio3a → legacy Get Started dropdown (the pre-experiment baseline).
  // Both stay hidden while the PostHog flag is still resolving, so neither
  // flashes in before the variant is known.
  const { variant: onboardingVariant, isLoading: onboardingVariantLoading } =
    useOnboardingVariant();
  const isScio3bVariant = !onboardingVariantLoading && onboardingVariant === 'scio3b';
  const isScio3aVariant = !onboardingVariantLoading && onboardingVariant === 'scio3a';

  // Wait for permissions to load before showing restricted nav items.
  // While me is loading, teamRole is undefined → hide restricted items to prevent
  // flash of forbidden links for read-only users (they appear then disappear).
  const isRestrictedRole =
    teamRole === ROLE_TYPES.READ_ONLY ||
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL ||
    teamRole === ROLE_TYPES.CONTENT_APPROVAL;

  const useNativeEngage = import.meta.env.VITE_USE_NATIVE_ENGAGE === 'true';
  const useNativeOptiDev = import.meta.env.VITE_USE_NATIVE_OPTIDEV === 'true';
  // Engage visible for all roles EXCEPT CONTENT_PROPOSAL & CONTENT_APPROVAL
  // (READ_ONLY users can browse but not edit)
  const isEngageRestrictedRole =
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL || teamRole === ROLE_TYPES.CONTENT_APPROVAL;
  const showEngage =
    permissionsLoaded &&
    !isEngageRestrictedRole &&
    variant === 'optisigns' &&
    (company?.feature?.engage || useNativeEngage);
  const showTemplates = permissionsLoaded && !isRestrictedRole && variant === 'optisigns';
  const showOptiDev = permissionsLoaded && !isRestrictedRole && useNativeOptiDev;
  // MDM is admin-only (spec 19 — Owner / Super Admin only in Phase A). The
  // route guard `MdmFeatureRoute` enforces this server-trip too; hiding the
  // top-nav link is the matching frontend half. Phase B will swap the gate
  // for a custom MDM Admin role check.
  //
  // Mass-rollout targeting: the kill-switch (appCfgs.mdmEnabled) broad rollout
  // surfaces the tab only to engaged users — a business email OR an account
  // that has paired its first device. Accounts explicitly whitelisted via
  // company.feature.mdm always see it regardless (Sales-onboarded — us + early
  // adopters). The screen-page "..." → Device Management item is the
  // unrestricted fallback for admins whose tab is hidden here.
  const isBusinessEmail = !!userEmail && !isFreeEmail(userEmail);
  const hasPairedFirstDevice = !!company?.payment?.firstDevicePaired;
  const passesRolloutTargeting = isBusinessEmail || hasPairedFirstDevice;
  const showDevices =
    permissionsLoaded &&
    isRootSuperAdmin &&
    (variant === 'optiedge' ||
      (variant === 'optisigns' && (hasMdmFeature || (mdmKillSwitchOn && passesRolloutTargeting))));
  // scio1 shows Analytics for all admin roles regardless of plan — Standard users see
  // Playback Report with an upgrade prompt. Removing the proofOfPlay gate matches scio1.
  // Hidden on `optiedge` variant (MDM-only bundle).
  const showAnalytics = permissionsLoaded && isRootAdmin && variant === 'optisigns';

  // Collapse levels:
  // 1: search → icon-only
  // 2: logo → icon-only + user menu → avatar-only
  // 3: notifications + Get Started pill hidden
  // 5: engage + analytics → "More" dropdown; help + order-devices hidden
  // 6: optidev → "More" dropdown
  const compactSearch = collapseLevel >= 1;
  const compactLogo = collapseLevel >= 2;
  const compactUser = collapseLevel >= 2;
  const hideNotifications = collapseLevel >= 3;
  // Help + Order Devices are primary actions — they survive two more
  // breakpoints than notifications so they stay visible as the nav narrows.
  const hideHelp = collapseLevel >= 5;
  const analyticsInMore = collapseLevel >= 5;
  const engageInMore = collapseLevel >= 5;
  const optidevInMore = collapseLevel >= 6;
  // Levels 7-10: progressively fold the primary nav items into More so
  // they don't slide behind the right cluster (search/pill/cart/avatar)
  // and become unreachable. Right-to-left order matches the visual order
  // of the nav — Templates first (rightmost of the "Screens..Templates"
  // block), then Schedules, Playlists, Files/Assets. Screens stays as
  // the visible anchor at every width.
  const templatesInMore = collapseLevel >= 7;
  const schedulesInMore = collapseLevel >= 8;
  const playlistsInMore = collapseLevel >= 9;
  const filesInMore = collapseLevel >= 10;
  // Get Started pill stays visible at ALL widths — losing sight of the
  // onboarding checklist confuses users. When the nav gets cramped
  // (level >= 6, i.e. OptiDev already moved to More) we switch the pill
  // to compact mode — just the progress ring, no "Get Started" text —
  // so it occupies ~34px instead of ~113-180px and never pushes the right
  // cluster into the primary nav items.
  const pillCompact = collapseLevel >= 6;

  // Track which optional items overflow into "More". `showTemplates` /
  // `showAssets` etc. follow the same role+feature gating as the inline
  // NavLinks below, so referencing them keeps the visibility logic identical
  // between the inline render and the More dropdown.
  const hasMoreItems =
    (showAnalytics && analyticsInMore) ||
    (showEngage && engageInMore) ||
    (showOptiDev && optidevInMore) ||
    (showTemplates && templatesInMore) ||
    schedulesInMore ||
    playlistsInMore ||
    filesInMore;

  return (
    <nav ref={navRef} className="sticky top-0 z-50 bg-muted">
      <div className="relative flex h-14 items-center overflow-visible px-2 md:px-4">
        {/* Left: Logo — routes to the variant's primary landing page */}
        <Link
          to={variant === 'optiedge' ? '/app/mdm' : '/app/screenManagement'}
          className="shrink-0 flex items-center mr-3"
        >
          {company?.brandingData?.logo ? (
            <img
              src={company.brandingData.logo as string}
              alt="Logo"
              className={compactLogo ? 'h-7 w-7 object-contain' : 'h-7'}
            />
          ) : compactLogo ? (
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
              O
            </span>
          ) : (
            <span className="text-lg font-bold text-primary">{t('OptiSigns')}</span>
          )}
        </Link>

        {/* Nav Items — horizontally scrollable on mobile, invisible no-op on desktop */}
        <div className="min-w-0 flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <div className="flex w-max items-center gap-0.5">
            {variant === 'optisigns' && (
              <>
                {/* Screens is the anchor — never folds into More. */}
                <NavLink to="/app/screenManagement" testId="nav-screens-link">
                  {t('Screens')}
                </NavLink>
                {!filesInMore && (
                  <NavLink to="/app/assetManagement" testId="nav-files-link">
                    {t('Files/Assets')}
                  </NavLink>
                )}
                {!playlistsInMore && (
                  <NavLink to="/app/playlistManagement" testId="nav-playlists-link">
                    {t('Playlists')}
                  </NavLink>
                )}
                {!schedulesInMore && (
                  <NavLink to="/app/scheduleManagement" testId="nav-schedules-link">
                    {t('Schedules')}
                  </NavLink>
                )}
              </>
            )}
            {showTemplates && !templatesInMore && (
              <NavLink to="/app/templateManagement" testId="nav-templates-link">
                {t('Templates')}
              </NavLink>
            )}

            {showEngage && !engageInMore && (
              <NavLink to="/app/engageManagement">{t('Engage')}</NavLink>
            )}
            {showOptiDev && !optidevInMore && <NavLink to="/app/optidev">{t('OptiDev')}</NavLink>}
            {/* Devices sits just before Analytics — init-doc mock §"Top menu". */}
            {showDevices && <NavLink to="/app/mdm">{t('Devices')}</NavLink>}
            {showAnalytics && !analyticsInMore && <AnalyticsDropdown isWhiteLabel={isWhiteLabel} />}

            {hasMoreItems && (
              <MoreDropdown
                showAnalytics={showAnalytics && analyticsInMore}
                showEngage={showEngage && engageInMore}
                showOptiDev={showOptiDev && optidevInMore}
                showTemplates={showTemplates && templatesInMore}
                showSchedules={schedulesInMore && variant === 'optisigns'}
                showPlaylists={playlistsInMore && variant === 'optisigns'}
                showFiles={filesInMore && variant === 'optisigns'}
                isWhiteLabel={isWhiteLabel}
              />
            )}
          </div>
        </div>

        {/* Right: Search + Get Started pill + Help + Notifications + User Menu.
            Pill sits to the RIGHT of the search bar (try-out spot — earlier
            it was right of the bell; revert is just moving the pill-slot
            block back below the NotificationDropdown). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <GlobalSearchBar compact={compactSearch} />
          {/* Pill slot — animates max-width so neighbors slide smoothly
              instead of snapping when the user minimizes/restores the
              checklist. Negative margin cancels the parent's gap-2 when
              the slot is collapsed, so no permanent gap is left behind. */}
          {!isWhiteLabel && (
            <div
              className={cn(
                // Plain block + overflow-hidden. The pill child renders at
                // its natural width starting from the slot's LEFT edge; as
                // the slot's max-width grows, more of the pill is revealed
                // from the LEFT side first (right side stays clipped until
                // the slot reaches the pill's full width). Matches Stripe's
                // Setup-guide reveal direction.
                'overflow-hidden',
                // Asymmetric transition: animate the slot OPENING (when
                // the pill appears on minimize) so the cluster slides
                // smoothly — but snap closed instantly on restore (no
                // point making the user watch a reverse animation before
                // they can use the panel). Removing the transition class
                // in the same render as the max-width change makes that
                // change non-animated.
                // Linear easing — uniform speed start to finish, no
                // deceleration at the end. Reads as a single steady slide.
                isPillVisible && 'transition-[max-width,margin] duration-[400ms] ease-linear',
                // Slot width: collapsed (no pill), compact (icon-only ~36px),
                // or full text pill (~180px). The transition runs on width
                // changes too, so the size-shift between compact and full
                // animates smoothly when the user resizes the viewport across
                // the level-6 threshold.
                !isPillVisible && 'max-w-0 -mx-1',
                isPillVisible && pillCompact && 'max-w-[40px]',
                isPillVisible && !pillCompact && 'max-w-[180px]',
              )}
            >
              {/* Conditionally mount the pill so it freshly mounts each
                  minimize cycle. Without this, the component stays mounted
                  with `return null` and its useRef(justMinimized) locks in
                  the app-startup value (always false), preventing the
                  slide-in animation from ever playing. */}
              {isPillVisible && <GetStartedPill compact={pillCompact} />}
            </div>
          )}
          {/* scio3b-split: Order Devices relocates from the Screens toolbar to
              the top nav for scio3b. scio3a keeps it in the toolbar.
              Not gated by a collapse level — it persists at every width, the
              same as the search bar. */}
          {!isWhiteLabel && isScio3bVariant && permissionsLoaded && isRootAdmin && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    asChild
                    aria-label={t('Order Devices')}
                  >
                    <Link to="/app/s/order-device" data-testid="nav-order-devices-btn">
                      <ShoppingCart className="h-4 w-4" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('Order Devices')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {!hideHelp && !isWhiteLabel && isScio3bVariant && <HelpDropdown />}
          {!hideHelp && !isWhiteLabel && isScio3aVariant && <GetStartedDropdown />}
          {!hideNotifications && <NotificationDropdown />}
          <UserMenu compact={compactUser} />
        </div>
      </div>
    </nav>
  );
}

const ANALYTICS_ROUTES = ['/app/playbackReport', '/app/ai-analytics'];

function AnalyticsDropdown({ isWhiteLabel }: { isWhiteLabel: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isActive = ANALYTICS_ROUTES.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'relative inline-flex h-auto items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:transition-colors',
            isActive
              ? 'text-foreground after:bg-primary'
              : 'text-muted-foreground after:bg-transparent hover:bg-sidebar-active hover:text-foreground focus-visible:bg-sidebar-active focus-visible:text-foreground focus-visible:after:bg-primary',
          )}
        >
          {t('Analytics')} <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={4}>
        <DropdownMenuItem asChild>
          <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
        </DropdownMenuItem>
        {!isWhiteLabel && (
          <DropdownMenuItem asChild>
            <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MoreDropdownProps {
  showAnalytics: boolean;
  showEngage: boolean;
  showOptiDev: boolean;
  showTemplates: boolean;
  showSchedules: boolean;
  showPlaylists: boolean;
  showFiles: boolean;
  isWhiteLabel: boolean;
}

function MoreDropdown({
  showAnalytics,
  showEngage,
  showOptiDev,
  showTemplates,
  showSchedules,
  showPlaylists,
  showFiles,
  isWhiteLabel,
}: MoreDropdownProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Core nav items (Files/Playlists/Schedules/Templates) are listed BEFORE
  // optional features (Engage/OptiDev/Analytics) inside the dropdown so the
  // most-frequently-accessed pages stay at the top when the cascade
  // collapses them in. A separator marks the boundary between the two
  // groups visually.
  const hasCoreItems = showFiles || showPlaylists || showSchedules || showTemplates;
  const hasOptionalItems = showEngage || showOptiDev || showAnalytics;

  const moreRoutes = [
    ...(showFiles ? ['/app/assetManagement'] : []),
    ...(showPlaylists ? ['/app/playlistManagement'] : []),
    ...(showSchedules ? ['/app/scheduleManagement'] : []),
    ...(showTemplates ? ['/app/templateManagement'] : []),
    ...(showEngage ? ['/app/engageManagement'] : []),
    ...(showAnalytics ? ANALYTICS_ROUTES : []),
    ...(showOptiDev ? ['/app/optidev'] : []),
  ];
  const isActive = moreRoutes.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'relative inline-flex h-auto items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:transition-colors',
            isActive
              ? 'text-foreground after:bg-primary'
              : 'text-muted-foreground after:bg-transparent hover:bg-sidebar-active hover:text-foreground focus-visible:bg-sidebar-active focus-visible:text-foreground focus-visible:after:bg-primary',
          )}
        >
          <span>{t('More')}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={4}>
        {/* Core nav items folded in by the cascade (levels 7-10) */}
        {showFiles && (
          <DropdownMenuItem asChild>
            <Link to="/app/assetManagement">{t('Files/Assets')}</Link>
          </DropdownMenuItem>
        )}
        {showPlaylists && (
          <DropdownMenuItem asChild>
            <Link to="/app/playlistManagement">{t('Playlists')}</Link>
          </DropdownMenuItem>
        )}
        {showSchedules && (
          <DropdownMenuItem asChild>
            <Link to="/app/scheduleManagement">{t('Schedules')}</Link>
          </DropdownMenuItem>
        )}
        {showTemplates && (
          <DropdownMenuItem asChild>
            <Link to="/app/templateManagement">{t('Templates')}</Link>
          </DropdownMenuItem>
        )}
        {/* Separator between core nav and optional features */}
        {hasCoreItems && hasOptionalItems && <DropdownMenuSeparator />}
        {showEngage && (
          <DropdownMenuItem asChild>
            <Link to="/app/engageManagement">{t('Engage')}</Link>
          </DropdownMenuItem>
        )}
        {showOptiDev && (
          <>
            {showEngage && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to="/app/optidev">{t('OptiDev')}</Link>
            </DropdownMenuItem>
          </>
        )}
        {showAnalytics && (
          <>
            {(showEngage || showOptiDev) && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
            </DropdownMenuItem>
            {!isWhiteLabel && (
              <DropdownMenuItem asChild>
                <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
